// src/pages/Dashboard.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";

import { authFetch } from "../utils/authFetch";
import {
  AccionFila, Boton, EncabezadoPagina, Iconos, Insignia, Tarjeta,
} from "../componentes/base";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export default function Dashboard() {
  const navigate = useNavigate();

  // ============================ AUTOFIND ===============================
  const [onusAutofind, setOnusAutofind] = useState([]);
  const [autofindConsultado, setAutofindConsultado] = useState(false);

  // Lista de OLTs (BD, barata) + detalles bajo demanda (SSH, pesada)
  const [olts, setOlts] = useState([]);
  const [oltInfoPorId, setOltInfoPorId] = useState({});    // id -> info consultada
  const [oltDetalle, setOltDetalle] = useState(null);      // {olt, info} en el modal
  const [loadingOltInfo, setLoadingOltInfo] = useState(null); // id consultándose
  const [loadingAutofind, setLoadingAutofind] = useState(false);

  // ============================ DASHBOARD (STATS + CHART) ===============================
  const [stats, setStats] = useState({ total: 0, online: 0, offline: 0 });
  const [chartData, setChartData] = useState([
    { mes: "Ene", onus: 0 },
    { mes: "Feb", onus: 0 },
    { mes: "Mar", onus: 0 },
    { mes: "Abr", onus: 0 },
    { mes: "May", onus: 0 },
    { mes: "Jun", onus: 0 },
    { mes: "Jul", onus: 0 },
    { mes: "Ago", onus: 0 },
    { mes: "Sep", onus: 0 },
    { mes: "Oct", onus: 0 },
    { mes: "Nov", onus: 0 },
    { mes: "Dic", onus: 0 },
  ]);
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  // Si por ahora tu dashboard es siempre la OLT 1, déjalo así.
  // Si luego lo haces dinámico, cambia este valor.
  const oltId = 1;

  // ============================================================
  // CARGAR AUTOFIND
  // ============================================================
  async function cargarAutofind() {
    try {
      setLoadingAutofind(true);

      const res = await authFetch(`/api/olt/autofind/${oltId}`);
      const data = await res.json();

      if (data.ok && Array.isArray(data.autofind)) {
        setOnusAutofind(data.autofind);
      } else {
        setOnusAutofind([]);
      }
    } catch (err) {
      console.log("Error obteniendo autofind:", err);
      setOnusAutofind([]);
    } finally {
      setLoadingAutofind(false);
      setAutofindConsultado(true);
    }
  }

  // ============================================================
  // CARGAR DASHBOARD (stats + chartData desde tabla onus)
  // ============================================================
  async function cargarDashboard() {
    try {
      setLoadingDashboard(true);

      const res = await authFetch(`/api/dashboard/olt/${oltId}`);
      const data = await res.json();

      if (data.ok) {
        if (data.stats) {
          setStats({
            total: Number(data.stats.total || 0),
            online: Number(data.stats.online || 0),
            offline: Number(data.stats.offline || 0),
          });
        } else {
          setStats({ total: 0, online: 0, offline: 0 });
        }

        if (Array.isArray(data.chartData)) {
          setChartData(data.chartData);
        } else {
          // fallback: mantener 12 meses en 0
          setChartData([
            { mes: "Ene", onus: 0 },
            { mes: "Feb", onus: 0 },
            { mes: "Mar", onus: 0 },
            { mes: "Abr", onus: 0 },
            { mes: "May", onus: 0 },
            { mes: "Jun", onus: 0 },
            { mes: "Jul", onus: 0 },
            { mes: "Ago", onus: 0 },
            { mes: "Sep", onus: 0 },
            { mes: "Oct", onus: 0 },
            { mes: "Nov", onus: 0 },
            { mes: "Dic", onus: 0 },
          ]);
        }
      } else {
        // si el backend regresa ok=false
        setStats({ total: 0, online: 0, offline: 0 });
      }
    } catch (err) {
      console.log("Error obteniendo dashboard:", err);
      setStats({ total: 0, online: 0, offline: 0 });
    } finally {
      setLoadingDashboard(false);
    }
  }

  // Lista de OLTs registradas (lectura de BD, no toca el OLT)
  async function cargarOlts() {
    try {
      const res = await authFetch("/api/olt-config/");
      const data = await res.json();
      if (data.status === "OK" && Array.isArray(data.olts)) {
        setOlts(data.olts);
      }
    } catch (e) {
      console.log("Error cargando lista de OLTs:", e);
    }
  }

  // Detalles del equipo (SSH real) — SOLO al presionar el ojito de la tabla.
  // Llena Temperatura/Encendida en la fila y deja el modal con las tarjetas.
  async function consultarOltInfo(olt) {
    try {
      setLoadingOltInfo(olt.id);
      const res = await authFetch(`/api/olt/info/${olt.id}`);
      const data = await res.json();
      if (data.ok) {
        setOltInfoPorId((prev) => ({ ...prev, [olt.id]: data }));
        setOltDetalle({ olt, info: data });
      } else {
        setOltDetalle({ olt, info: null, error: data.error || "Sin respuesta del OLT" });
      }
    } catch (e) {
      setOltDetalle({ olt, info: null, error: "Error de red consultando el OLT" });
    } finally {
      setLoadingOltInfo(null);
    }
  }

  useEffect(() => {
    cargarDashboard();
    cargarOlts();
    // ✅ El autofind ya NO se consulta al abrir la página (es una sesión SSH
    //    pesada al OLT). Se consulta solo con el botón "Consultar OLT".

    window.addEventListener("error", (e) => {
      console.log("[window.error]", {
        message: e?.message,
        source: e?.filename,
        line: e?.lineno,
        col: e?.colno,
        stack: e?.error?.stack,
      });
    });
  }, []);

  // =======================================================================
  return (
    <div className="dashboard">
      <EncabezadoPagina
        icono={Iconos.panel}
        titulo="Panel"
        resumen={
          loadingDashboard
            ? "Cargando…"
            : `${stats.online} de ${stats.total} ONUs en línea · ${olts.length} OLT${olts.length === 1 ? "" : "s"}`
        }
        acciones={
          <Boton
            variante="secundario"
            icono={Iconos.actualizar}
            cargando={loadingDashboard}
            onClick={cargarDashboard}
          >
            Actualizar
          </Boton>
        }
      />

      {/* ====================== CARDS SUPERIORES ====================== */}
      <div className="dashboard-cards">
        <div className="stat-card">
          <div className="stat-icon primary"><Iconos.onus size={22} strokeWidth={1.8} /></div>
          <div className="stat-info">
            <span className="stat-label">ONU&apos;s Totales</span>
            <span className="stat-value">
              {loadingDashboard ? "…" : stats.total}
            </span>
            <span className="stat-sub">Registradas en el sistema</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon success"><Iconos.enLinea size={22} strokeWidth={1.8} /></div>
          <div className="stat-info">
            <span className="stat-label">ONU&apos;s Online</span>
            <span className="stat-value">
              {loadingDashboard ? "…" : stats.online}
            </span>
            <span className="stat-sub">Conectadas actualmente</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon warning"><Iconos.fueraDeLinea size={22} strokeWidth={1.8} /></div>
          <div className="stat-info">
            <span className="stat-label">ONU&apos;s Offline</span>
            <span className="stat-value">
              {loadingDashboard ? "…" : stats.offline}
            </span>
            <span className="stat-sub">Sin señal o apagadas</span>
          </div>
        </div>

        <div className="stat-card highlight">
          <div className="stat-icon"><Iconos.olt size={22} strokeWidth={1.8} /></div>
          <div className="stat-info">
            <span className="stat-label">OLT&apos;s registradas</span>
            <span className="stat-value">{olts.length}</span>
            <span className="stat-sub">Detalles en la lista de abajo</span>
          </div>
        </div>
      </div>

      {/* ============ MODAL DETALLES DEL OLT (consulta bajo demanda) ============ */}
      {oltDetalle && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setOltDetalle(null)}
        >
          <div
            style={{
              background: "#fff", borderRadius: "14px", padding: "22px 24px",
              width: "min(480px, 92vw)", maxHeight: "80vh", overflowY: "auto",
              boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 4px" }}>
              OLT #{oltDetalle.olt.id} · {oltDetalle.olt.nombre || oltDetalle.olt.host}
            </h3>
            <p style={{ margin: "0 0 14px", color: "#555", fontSize: "14px" }}>
              {oltDetalle.olt.host}
            </p>

            {oltDetalle.error ? (
              <p style={{ color: "#dc2626" }}>{oltDetalle.error}</p>
            ) : (
              <>
                <p style={{ margin: "4px 0" }}>
                  <strong>Versión:</strong> {oltDetalle.info.version || "—"}
                  {oltDetalle.info.patch ? `  (parche ${oltDetalle.info.patch})` : ""}
                </p>
                <p style={{ margin: "4px 0" }}>
                  <strong>Encendido desde hace:</strong> {oltDetalle.info.uptime || "—"}
                </p>

                <h4 style={{ margin: "14px 0 6px" }}>Tarjetas (frame 0)</h4>
                {(oltDetalle.info.boards || []).length === 0 ? (
                  <p style={{ color: "#777" }}>
                    No se pudieron interpretar las tarjetas. Salida cruda abajo.
                  </p>
                ) : (
                  <table style={{ width: "100%", fontSize: "14px", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ textAlign: "left", borderBottom: "1px solid #ddd" }}>
                        <th>Slot</th><th>Tarjeta</th><th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {oltDetalle.info.boards.map((b) => (
                        <tr key={b.slot} style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td>{b.slot}</td>
                          <td>{b.tarjeta}</td>
                          <td style={{
                            color: String(b.estado).toLowerCase().includes("normal")
                              ? "#16a34a" : "#dc2626",
                            fontWeight: 600,
                          }}>
                            {b.estado}{b.extra ? ` ${b.extra}` : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <details style={{ marginTop: "12px" }}>
                  <summary style={{ cursor: "pointer", color: "#555", fontSize: "13px" }}>
                    Ver salida cruda del OLT
                  </summary>
                  <pre style={{
                    fontSize: "11px", background: "#f4f6f8", padding: "10px",
                    borderRadius: "8px", overflowX: "auto", whiteSpace: "pre-wrap",
                  }}>
{oltDetalle.info.raw_version}
{"\n"}
{oltDetalle.info.raw_board}
                  </pre>
                </details>
              </>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
              <Boton
                variante="secundario"
                icono={Iconos.cerrar}
                onClick={() => setOltDetalle(null)}
              >
                Cerrar
              </Boton>
            </div>
          </div>
        </div>
      )}

      {/* ====================== GRAFICA + AUTOFIND ====================== */}
      <div className="dashboard-row">
        {/* GRAFICA */}
        <div className="dashboard-chart">
          <h3>ONU&apos;s Autorizadas en el Sistema</h3>

          {loadingDashboard ? (
            <div className="loading-box">
              <div className="spinner"></div>
              <p>Cargando estadísticas…</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="onus" fill="#1e90ff" />
              </BarChart>
            </ResponsiveContainer>
          )}

          {/* El botón de recargar vive ahora en el encabezado de la página */}
        </div>

        {/* ===================== ONUS NO AUTORIZADAS ====================== */}
        <div className="dashboard-card">
          <h3>ONU&apos;s No Autorizadas</h3>

          {loadingAutofind ? (
            <div className="loading-box">
              <div className="spinner"></div>
              <p>Consultando ONUs en la OLT…</p>
            </div>
          ) : !autofindConsultado ? (
            <>
              <p className="empty">Presiona para consultar la OLT.</p>
              <Boton icono={Iconos.buscar} onClick={cargarAutofind}>
                Consultar OLT
              </Boton>
            </>
          ) : onusAutofind.length === 0 ? (
            <>
              <p className="empty">Sin ONUs pendientes</p>
              <Boton
                variante="secundario"
                icono={Iconos.actualizar}
                onClick={cargarAutofind}
              >
                Actualizar
              </Boton>
            </>
          ) : (
            <>
              <ul className="onus-list">
                {onusAutofind.slice(0, 3).map((o, i) => (
                  <li key={i} className="onu-item">
                    <strong>SN:</strong> {o.sn} <br />
                    <strong>PON:</strong> {o.puerto} <br />
                    <strong>Modelo:</strong> {o.model}
                  </li>
                ))}
              </ul>

              {/* BOTÓN — IR A LA PÁGINA COMPLETA (lleva la lista ya consultada) */}
              <Boton
                icono={Iconos.adelante}
                onClick={() =>
                  navigate("/onus-no-autorizadas", {
                    state: { onus: onusAutofind },
                  })
                }
              >
                Ver todas ({onusAutofind.length})
              </Boton>

              {/* BOTÓN ACTUALIZAR */}
              <Boton
                variante="secundario"
                icono={Iconos.actualizar}
                cargando={loadingAutofind}
                onClick={cargarAutofind}
              >
                Actualizar
              </Boton>
            </>
          )}
        </div>
      </div>

      {/* ====================== TABLA OLT (solo para listar, sin info real) ====================== */}
      <Tarjeta titulo="Lista de OLT's" sinEspacio>
        <div className="olt-row header">
          <span>OLT</span>
          <span>Nombre</span>
          <span>ONU&apos;s Autorizadas</span>
          <span>Estado</span>
          <span>Temperatura</span>
          <span>Encendida</span>
          <span>Acción</span>
        </div>

        {/* ✅ Filas REALES desde olt_config. Temperatura y Encendida se llenan
            al presionar el 👁️ (consulta SSH bajo demanda, no al abrir). */}
        {olts.length === 0 ? (
          <div className="olt-row">
            <span>Sin OLTs registradas</span>
          </div>
        ) : (
          olts.map((o) => {
            const info = oltInfoPorId[o.id];
            const online = String(o.ultimo_estado || "").toLowerCase() === "online";
            return (
              <div className="olt-row" key={o.id}>
                <div className="olt-info">
                  <div className="olt-image-placeholder">HW</div>
                  <div className="olt-text">
                    <span className="olt-model">
                      {info?.version || "Huawei"}
                    </span>
                    <span className="olt-brand">#{o.id} · {o.host}</span>
                  </div>
                </div>

                <span>{o.nombre || "—"}</span>
                <span>{loadingDashboard ? "…" : stats.total}</span>
                <span>
                  {/* Antes el estado "offline" no tenía color propio: la
                      pastilla salía en gris y se confundía con "sin dato". */}
                  <Insignia
                    variante={online ? "activo" : "cortado"}
                    icono={online ? Iconos.enLinea : Iconos.fueraDeLinea}
                    texto={online ? "En línea" : (o.ultimo_estado || "Fuera de línea")}
                  />
                </span>
                <span>
                  {info
                    ? (info.temperatura !== null && info.temperatura !== undefined
                        ? `${info.temperatura} °C`
                        : "N/D")
                    : "--"}
                </span>
                <span>{info ? (info.uptime || "N/D") : "--"}</span>
                <AccionFila
                  icono={loadingOltInfo === o.id ? Iconos.cargando : Iconos.info}
                  titulo="Consultar detalles en el OLT"
                  onClick={() => consultarOltInfo(o)}
                  disabled={loadingOltInfo !== null}
                />
              </div>
            );
          })
        )}
      </Tarjeta>
    </div>
  );
}

